import BaseAudioVisualizer from "./BaseAudioVisualizer";

interface WAVAudioVisualizerOptions {
  audioUrl: string;
  onIsPlayingChange?: (isPlaying: boolean) => void;
}
class WAVAudioVisualizer extends BaseAudioVisualizer {
  private audioSource: AudioBufferSourceNode | null = null;
  private audioBuffer: AudioBuffer | null = null;
  private startTime: number = 0;
  private pausedAt: number = 0;
  private isPlaying: boolean = false;
  private audioUrl: string;
  private onIsPlayingChange;

  constructor(
    canvasElement: HTMLCanvasElement,
    options: WAVAudioVisualizerOptions
  ) {
    super(canvasElement);
    this.onIsPlayingChange = options?.onIsPlayingChange;
    this.audioUrl = options?.audioUrl;
  }

  private async loadAndPlayAudio(): Promise<void> {
    try {
      const response = await fetch(this.audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      this.stop(); // Stop any existing playback
      this.startAudio();
    } catch (error) {
      console.error("Error loading or playing audio:", error);
    }
  }

  private startAudio(): void {
    if (!this.audioBuffer) return;

    this.audioSource = this.audioContext.createBufferSource();
    this.audioSource.buffer = this.audioBuffer;

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;

    this.audioSource.connect(this.analyser);
    this.analyser.connect(this.audioContext.destination);

    this.audioSource.start(0, this.pausedAt);
    this.startTime = this.audioContext.currentTime - this.pausedAt;
    this.setPlaying(true);
    this.start();

    this.audioSource.onended = () => {
      if (this.isPlaying) {
        this.stop();
      }
    };
  }

  public toggle(): void {
    if (!this.isPlaying) {
      if (!this.audioBuffer) {
        this.loadAndPlayAudio();
      } else {
        this.resume();
      }
    } else {
      this.pause();
    }
  }

  private pause(): void {
    if (this.isPlaying) {
      this.stop();
      this.pausedAt = this.audioContext.currentTime - this.startTime;
      this.setPlaying(false);
    }
  }

  private resume(): void {
    if (!this.isPlaying && this.audioBuffer) {
      this.startAudio();
    }
  }

  public stop(): void {
    super.stop();
    this.cleanupAudioNodes();
    this.pausedAt = 0;
    this.setPlaying(false);
  }

  private setPlaying(isPlaying: boolean) {
    this.isPlaying = isPlaying;
    this.onIsPlayingChange && this.onIsPlayingChange(isPlaying);
  }

  protected async setupAudioSource(): Promise<void> {
    // This method is empty because we set up the audio source in loadAndPlayAudio
  }

  protected cleanupAudioNodes(): void {
    if (this.audioSource) {
      this.audioSource.stop();
      this.audioSource.disconnect();
      this.audioSource = null;
    }
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }
  }
}

export default WAVAudioVisualizer;
